import { addToRecord, objectWithoutKeys, updateRecord } from '@ms/yammer-data-support';

import type {
  PublishersBeginReplyToDirectMessageAction,
  PublishersBeginReplyToGroupMessageAction,
  PublishersBeginReplyToNetworkQuestionMessageAction,
  PublishersBeginReplyToTeamsMeetingMessageAction,
  PublishersBeginReplyToUserStorylineMessageAction,
  PublishersOpenDirectNewAction,
  PublishersOpenDirectReplyAction,
  PublishersOpenEditMessageAction,
  PublishersOpenEventNewQuestionAction,
  PublishersOpenEventNewUpdateAction,
  PublishersOpenGroupCopilotSuggestedQuestionAction,
  PublishersOpenGroupNewArticleAction,
  PublishersOpenGroupNewPollAction,
  PublishersOpenGroupNewPraiseAction,
  PublishersOpenGroupNewQuestionAction,
  PublishersOpenGroupNewUpdateAction,
  PublishersOpenGroupReplyAction,
  PublishersOpenNetworkQuestionNewQuestionAction,
  PublishersOpenNetworkQuestionReplyAction,
  PublishersOpenShareNewDirectAction,
  PublishersOpenShareToNewUpdateAction,
  PublishersOpenTeamsMeetingNewQuestionAction,
  PublishersOpenTeamsMeetingNewUpdateAction,
  PublishersOpenTeamsMeetingReplyAction,
  PublishersOpenUserNewArticleAction,
  PublishersOpenUserNewMediaAction,
  PublishersOpenUserNewPollAction,
  PublishersOpenUserNewPraiseAction,
  PublishersOpenUserNewQuestionAction,
  PublishersOpenUserNewUpdateAction,
  PublishersOpenUserReplyAction,
  SetPublisherQuestionPromptAction,
} from '../../domains/publishers/actions.types';
import type { Topic } from '../types';

import { getInlineImageAttachmentIds } from './getInlineImageAttachmentIds';
import type { PublishersAction } from './reducer.types';
import type {
  BaseReplyPublisher,
  DirectPublisher,
  EditMessagePublisher,
  EventPublisher,
  GroupNewArticlePublisher,
  GroupNewPollPublisher,
  GroupNewPraisePublisher,
  GroupNewQuestionPublisher,
  GroupNewUpdatePublisher,
  GroupReplyPublisher,
  NetworkQuestionNewQuestionPublisher,
  NetworkQuestionReplyPublisher,
  Publisher,
  Publishers,
  TeamsMeetingNewQuestionPublisher,
  TeamsMeetingNewUpdatePublisher,
  TeamsMeetingReplyPublisher,
  UserNewArticlePublisher,
  UserNewMediaPublisher,
  UserNewPollPublisher,
  UserNewPraisePublisher,
  UserNewQuestionPublisher,
  UserNewUpdatePublisher,
  UserReplyPublisher,
} from './types';
const createPublisher = (id: Publisher['id'], defaultTopicId?: Topic['id']) => ({
  id,
  attachments: [],
  inlineAttachmentIds: [],
  notifiedUserIds: [],
  hasAttemptedToAttachFile: false,
  headerImage: undefined,
  isGifPickerOpen: false,
  isTopicBarOpen: false,
  isTopicBarDismissed: false,
  postType: 'GroupNewUpdate',
  isExpanded: true,
  changedGroupWhileFilesAttached: false,
  changedPostOnBehalfOfUserWhileFilesAttached: false,
  topicNames: [],
  topicIds: defaultTopicId ? [defaultTopicId] : [],
  wasPublishedAsDraft: false,
  questionPromptState: undefined,
  campaignReferences: [],
  isMandatoryNotification: false,
});
const defaultPollOptions = ['', '', ''];
const createThreadStarterPublisher = (id: Publisher['id'], defaultTopicId?: Topic['id']) => ({
  ...createPublisher(id, defaultTopicId),
  praisedUserIds: [],
  pollOptions: defaultPollOptions,
});
const openGroupNewUpdatePublisher = (
  state: Publishers,
  {
    id,
    groupId,
    sharedMessageId,
    publisherInstanceId,
    onBehalfOfSenderId,
    topicId,
    campaignId,
    isTopicBarOpen,
    sharedTeamsMeetingId,
    communicationsProjectSlotId,
  }: PublishersOpenGroupNewUpdateAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewUpdatePublisher = {
    ...createThreadStarterPublisher(id, topicId),
    publisherInstanceId,
    groupId,
    sharedMessageId,
    isAnnouncement: false,
    topicId,
    communicationsProjectSlotId,
    ...existingState,
    isExpanded: true,
    postType: 'GroupNewUpdate',
    destination: 'Group',
    contentType: 'Regular',
    isEditing: false,
    isReply: false,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignId,
    campaignReferences: [],
    isTopicBarOpen,
    sharedTeamsMeetingId,
  };

  return addToRecord(state, id, publisher);
};
const openUserNewUpdatePublisher = (
  state: Publishers,
  {
    id,
    userId,
    sharedMessageId,
    publisherInstanceId,
    onBehalfOfSenderId,
    campaignId,
    isTopicBarOpen,
    sharedTeamsMeetingId,
  }: PublishersOpenUserNewUpdateAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserNewUpdatePublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    sharedMessageId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: 'UserNewUpdate',
    destination: 'User',
    contentType: 'Regular',
    isEditing: false,
    isReply: false,
    isMandatoryNotification: false,
    onBehalfOfSenderId,
    campaignId,
    campaignReferences: [],
    isTopicBarOpen,
    sharedTeamsMeetingId,
  };

  return addToRecord(state, id, publisher);
};
const openUserNewMediaPublisher = (
  state: Publishers,
  {
    id,
    userId,
    sharedMessageId,
    publisherInstanceId,
    onBehalfOfSenderId,
    isTopicBarOpen,
  }: PublishersOpenUserNewMediaAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserNewMediaPublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    sharedMessageId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: 'UserNewMedia',
    destination: 'User',
    contentType: 'Regular',
    isEditing: false,
    isReply: false,
    isMandatoryNotification: false,
    onBehalfOfSenderId,
    campaignReferences: [],
    isTopicBarOpen,
  };

  return addToRecord(state, id, publisher);
};
const openUserNewQuestionPublisher = (
  state: Publishers,
  {
    id,
    publisherInstanceId,
    onBehalfOfSenderId,
    userId,
    campaignId,
    isTopicBarOpen,
    title,
  }: PublishersOpenUserNewQuestionAction['payload']
) => {
  const existingState: Publisher | Pick<Publisher, 'title'> = state[id] ? state[id] : {};
  const currentTitle = title ?? existingState.title;
  const publisher: UserNewQuestionPublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: 'UserNewQuestion',
    destination: 'User',
    contentType: 'Question',
    isEditing: false,
    isReply: false,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignId,
    campaignReferences: [],
    isTopicBarOpen,
    title: currentTitle,
  };

  return addToRecord(state, id, publisher);
};
const openUserNewPollPublisher = (
  state: Publishers,
  {
    id,
    publisherInstanceId,
    onBehalfOfSenderId,
    userId,
    campaignId,
    isTopicBarOpen,
  }: PublishersOpenUserNewPollAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserNewPollPublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    isAnnouncement: false,
    ...existingState,
    pollOptions: defaultPollOptions,
    isExpanded: true,
    postType: 'UserNewPoll',
    destination: 'User',
    contentType: 'Poll',
    isEditing: false,
    isReply: false,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignId,
    campaignReferences: [],
    isTopicBarOpen,
  };

  return addToRecord(state, id, publisher);
};
const openUserNewArticlePublisher = (
  state: Publishers,
  { id, publisherInstanceId, onBehalfOfSenderId, userId, campaignId }: PublishersOpenUserNewArticleAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserNewArticlePublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: 'UserNewArticle',
    destination: 'User',
    contentType: 'Article',
    isEditing: false,
    isReply: false,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignId,
    campaignReferences: [],
  };

  return addToRecord(state, id, publisher);
};
const openUserNewPraisePublisher = (
  state: Publishers,
  {
    id,
    publisherInstanceId,
    userId,
    campaignId,
    praisedUserIds = [],
    onBehalfOfSenderId,
    isTopicBarOpen,
  }: PublishersOpenUserNewPraiseAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserNewPraisePublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: 'UserNewPraise',
    destination: 'User',
    contentType: 'Praise',
    isEditing: false,
    isReply: false,
    praisedUserIds,
    isMandatoryNotification: false,
    campaignId,
    onBehalfOfSenderId,
    campaignReferences: [],
    isTopicBarOpen,
  };

  return addToRecord(state, id, publisher);
};
const openShareNewDirectPublisher = (
  state: Publishers,
  { id, sharedMessageId, publisherInstanceId }: PublishersOpenShareNewDirectAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: DirectPublisher = {
    ...createPublisher(id),
    publisherInstanceId,
    sharedMessageId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: 'DirectNew',
    destination: 'Direct',
    contentType: 'Regular',
    isEditing: false,
    isReply: false,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
  };

  return addToRecord(state, id, publisher);
};
const openGroupNewQuestionPublisher = (
  state: Publishers,
  {
    id,
    groupId,
    publisherInstanceId,
    onBehalfOfSenderId,
    topicId,
    campaignId,
    isTopicBarOpen,
    title,
    rawContentState,
  }: PublishersOpenGroupNewQuestionAction['payload']
) => {
  const existingState = state[id] ? state[id] : { rawContentState: undefined };
  const currentRawContentState = rawContentState || existingState.rawContentState;
  const currentTitle = title ?? ('title' in existingState ? existingState.title : undefined);
  const publisher: GroupNewQuestionPublisher = {
    ...createThreadStarterPublisher(id, topicId),
    publisherInstanceId,
    groupId,
    isAnnouncement: false,
    topicId,
    ...existingState,
    isExpanded: true,
    postType: 'GroupNewQuestion',
    destination: 'Group',
    contentType: 'Question',
    isEditing: false,
    isReply: false,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignId,
    campaignReferences: [],
    isTopicBarOpen,
    title: currentTitle,
    rawContentState: currentRawContentState,
  };

  return addToRecord(state, id, publisher);
};
const openGroupCopilotSuggestedQuestionPublisher = (
  state: Publishers,
  {
    id,
    groupId,
    title,
    publisherInstanceId,
    topicId,
    isTopicBarOpen,
  }: PublishersOpenGroupCopilotSuggestedQuestionAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewQuestionPublisher = {
    ...createThreadStarterPublisher(id, topicId),
    publisherInstanceId,
    groupId,
    isAnnouncement: false,
    topicId,
    ...existingState,
    title,
    isExpanded: true,
    postType: 'GroupNewQuestion',
    destination: 'Group',
    contentType: 'Question',
    isEditing: false,
    isMandatoryNotification: false,
    isTopicBarOpen,
    isReply: false,
  };

  return addToRecord(state, id, publisher);
};
const openUserNewQuestionPublisherWrapper = (
  state: Publishers,
  payload: PublishersOpenUserNewQuestionAction['payload']
) => openUserNewQuestionPublisher(state, payload);
const openGroupNewQuestionPublisherWrapper = (
  state: Publishers,
  payload: PublishersOpenGroupNewQuestionAction['payload']
) => openGroupNewQuestionPublisher(state, payload);
const openGroupNewArticlePublisher = (
  state: Publishers,
  {
    id,
    groupId,
    publisherInstanceId,
    onBehalfOfSenderId,
    topicId,
    campaignId,
  }: PublishersOpenGroupNewArticleAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewArticlePublisher = {
    ...createThreadStarterPublisher(id, topicId),
    publisherInstanceId,
    groupId,
    isAnnouncement: false,
    topicId,
    ...existingState,
    isExpanded: true,
    postType: 'GroupNewArticle',
    destination: 'Group',
    contentType: 'Article',
    isEditing: false,
    isReply: false,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    isTopicBarOpen: topicId != null,
    isTopicBarDismissed: false,
    campaignId,
    campaignReferences: [],
  };

  return addToRecord(state, id, publisher);
};
const openGroupNewPollPublisher = (
  state: Publishers,
  {
    id,
    groupId,
    publisherInstanceId,
    onBehalfOfSenderId,
    campaignId,
    isTopicBarOpen,
  }: PublishersOpenGroupNewPollAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewPollPublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    groupId,
    isAnnouncement: false,
    ...existingState,
    pollOptions: defaultPollOptions,
    isExpanded: true,
    postType: 'GroupNewPoll',
    destination: 'Group',
    contentType: 'Poll',
    isEditing: false,
    isReply: false,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignId,
    campaignReferences: [],
    isTopicBarOpen,
  };

  return addToRecord(state, id, publisher);
};
const openGroupNewPraisePublisher = (
  state: Publishers,
  {
    id,
    groupId,
    publisherInstanceId,
    onBehalfOfSenderId,
    campaignId,
    isTopicBarOpen,
    praisedUserIds = [],
  }: PublishersOpenGroupNewPraiseAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewPraisePublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    groupId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: 'GroupNewPraise',
    destination: 'Group',
    contentType: 'Praise',
    isEditing: false,
    isReply: false,
    praisedUserIds,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignId,
    campaignReferences: [],
    isTopicBarOpen,
  };

  return addToRecord(state, id, publisher);
};
const openGroupReplyPublisher = (
  state: Publishers,
  {
    id,
    groupId,
    threadId,
    emptyPublishers,
    publisherInstanceId,
    rawContentState,
    onBehalfOfSenderId,
    agentProposalItemId,
  }: PublishersOpenGroupReplyAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupReplyPublisher = {
    ...createPublisher(id),
    ...existingState,
    isExpanded: true,
    publisherInstanceId,
    groupId,
    threadId,
    postType: 'GroupReply',
    destination: 'Group',
    contentType: 'Regular',
    isEditing: false,
    isReply: true,
    isAnnouncement: false,
    isSecondLevelReply: false,
    rawContentState,
    onBehalfOfSenderId,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
    agentProposalItemId,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};
const openDirectNewPublisher = (
  state: Publishers,
  { id, publisherInstanceId, rawContentState }: PublishersOpenDirectNewAction['payload']
) => {
  const publisher: DirectPublisher = {
    ...createPublisher(id),
    publisherInstanceId,
    postType: 'DirectNew',
    destination: 'Direct',
    contentType: 'Regular',
    isEditing: false,
    isReply: false,
    isAnnouncement: false,
    rawContentState,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
  };

  return addToRecord(state, id, publisher);
};
const openUserReplyPublisher = (
  state: Publishers,
  {
    id,
    userId,
    threadId,
    emptyPublishers,
    publisherInstanceId,
    rawContentState,
    agentProposalItemId,
  }: PublishersOpenUserReplyAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserReplyPublisher = {
    ...createPublisher(id),
    ...existingState,
    isExpanded: true,
    publisherInstanceId,
    userId,
    threadId,
    postType: 'UserReply',
    destination: 'User',
    contentType: 'Regular',
    isEditing: false,
    isReply: true,
    isAnnouncement: false,
    isSecondLevelReply: false,
    rawContentState,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
    agentProposalItemId,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};
const openDirectReplyPublisher = (
  state: Publishers,
  { id, threadId, emptyPublishers, publisherInstanceId, rawContentState }: PublishersOpenDirectReplyAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: DirectPublisher & BaseReplyPublisher = {
    ...createPublisher(id),
    ...existingState,
    isExpanded: true,
    publisherInstanceId,
    threadId,
    postType: 'DirectReply',
    destination: 'Direct',
    contentType: 'Regular',
    isEditing: false,
    isReply: true,
    isAnnouncement: false,
    isSecondLevelReply: false,
    rawContentState,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};
const openNetworkQuestionReplyPublisher = (
  state: Publishers,
  {
    id,
    threadId,
    emptyPublishers,
    publisherInstanceId,
    rawContentState,
  }: PublishersOpenNetworkQuestionReplyAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: NetworkQuestionReplyPublisher = {
    ...createPublisher(id),
    ...existingState,
    isExpanded: true,
    publisherInstanceId,
    threadId,
    postType: 'NetworkQuestionReply',
    destination: 'NetworkQuestion',
    contentType: 'Regular',
    isEditing: false,
    isReply: true,
    isAnnouncement: false,
    isSecondLevelReply: false,
    rawContentState,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};
const openTeamsMeetingReplyPublisher = (
  state: Publishers,
  {
    id,
    threadId,
    emptyPublishers,
    publisherInstanceId,
    rawContentState,
    moderatedEventAgentReplyId,
    attachments,
    notifiedUserIds,
    sharedMessageId,
    currentlyReplyingToId,
    isSecondLevelReply = false,
  }: PublishersOpenTeamsMeetingReplyAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: TeamsMeetingReplyPublisher = {
    ...createPublisher(id),
    ...existingState,
    isExpanded: true,
    publisherInstanceId,
    threadId,
    postType: 'TeamsMeetingReply',
    destination: 'TeamsMeeting',
    contentType: 'Regular',
    isEditing: false,
    isReply: true,
    isAnnouncement: false,
    isSecondLevelReply,
    rawContentState,
    isTopicBarOpen: false,
    isBestReply: false,
    isModeratorMessage: false,
    isAnonymousMessage: false,
    ...(moderatedEventAgentReplyId
      ? {
          moderatedEventAgentReplyId,
          attachments: attachments ?? [],
          inlineAttachmentIds: getInlineImageAttachmentIds(rawContentState),
          notifiedUserIds: notifiedUserIds ?? [],
          sharedMessageId,
          currentlyReplyingToId,
          onBehalfOfSenderId: undefined,
        }
      : {}),
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};
const openEditMessagePublisher = (
  state: Publishers,
  {
    id,
    messageId,
    notifiedUserIds,
    publisherInstanceId,
    praisedUserIds,
    attachments,
    announcementAudience,
    announcementAudienceIds,
    announcementTemporaryAudienceIds,
    announcementNotificationTargets,
    announcementNotificationTargetEntityReferences,
    isExpanded,
    rawContentState,
    title,
    onBehalfOfSenderId,
    headerImage,
    isAnnouncement = false,
    shouldShowTitle = false,
    isConvertingToQuestion = false,
    isDiscussionEdit,
    isMandatoryNotification = false,
    pollOptions,
    sharedMessageId,
  }: PublishersOpenEditMessageAction['payload']
) => {
  const publisher: EditMessagePublisher = {
    ...createPublisher(id),
    attachments,
    inlineAttachmentIds: getInlineImageAttachmentIds(rawContentState),
    sharedMessageId,
    announcementAudience,
    announcementAudienceIds,
    announcementTemporaryAudienceIds,
    announcementNotificationTargets,
    announcementNotificationTargetEntityReferences,
    publisherInstanceId,
    messageId,
    notifiedUserIds,
    originalNotifiedUserIds: notifiedUserIds,
    postType: 'Edit',
    destination: 'Group',
    contentType: 'Regular',
    isEditing: true,
    isReply: false,
    isAnnouncement,
    praisedUserIds,
    isExpanded,
    rawContentState,
    title,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
    isMandatoryNotification,
    hasRemovedAttachmentsNotDeletedFromSharePoint: false,
    onBehalfOfSenderId,
    headerImage,
    shouldShowTitle,
    isConvertingToQuestion,
    ...(isDiscussionEdit ? { isDiscussionEdit } : {}),
    pollOptions,
  };

  return addToRecord(state, id, publisher);
};
const openShareToNewUpdatePublisher = (
  state: Publishers,
  { id, publisherInstanceId }: PublishersOpenShareToNewUpdateAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewUpdatePublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    isAnnouncement: false,
    groupId: null,
    ...existingState,
    isExpanded: true,
    attachments: [],
    postType: 'GroupNewUpdate',
    destination: 'Group',
    contentType: 'Regular',
    isEditing: false,
    isReply: false,
  };

  return addToRecord(state, id, publisher);
};
const openNetworkQuestionNewQuestionPublisher = (
  state: Publishers,
  { id, publisherInstanceId, title, rawContentState }: PublishersOpenNetworkQuestionNewQuestionAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: NetworkQuestionNewQuestionPublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    isAnnouncement: false,
    title,
    rawContentState,
    ...existingState,
    isExpanded: true,
    postType: 'NetworkQuestionNewQuestion',
    destination: 'NetworkQuestion',
    contentType: 'Question',
    isEditing: false,
    isReply: false,
    isMandatoryNotification: false,
    isTopicBarOpen: true,
    isTopicBarDismissed: false,
    hasCollapsedRelatedQuestionsModule: false,
  };

  return addToRecord(state, id, publisher);
};
const createEventPublisher = (id: Publisher['id']) => ({
  attachments: [],
  inlineAttachmentIds: [],
  hasAttemptedToAttachFile: false,
  changedGroupWhileFilesAttached: false,
  changedPostOnBehalfOfUserWhileFilesAttached: false,
  headerImage: undefined,
  id,
  isAnnouncement: false,
  isExpanded: true,
  isGifPickerOpen: false,
  isTopicBarOpen: false,
  isTopicBarDismissed: false,
  notifiedUserIds: [],
  praisedUserIds: [],
  pollOptions: [],
  topicNames: [],
  topicIds: [],
  campaignReferences: [],
  isMandatoryNotification: false,
});
type OpenEventNewUpdatePublisher = (
  state: Publishers,
  payload: PublishersOpenEventNewUpdateAction['payload'] | PublishersOpenEventNewQuestionAction['payload']
) => Publishers;
const openEventNewUpdatePublisher: OpenEventNewUpdatePublisher = (
  state,
  { id, groupId, eventId, publisherInstanceId, onBehalfOfSenderId }
) => {
  const publisher: EventPublisher = {
    ...createEventPublisher(id),
    publisherInstanceId,
    eventId,
    groupId,
    onBehalfOfSenderId,
    postType: 'EventNewUpdate',
    destination: 'Event',
    contentType: 'Regular',
    isEditing: false,
    isReply: false,
  };

  return {
    ...state,
    [id]: publisher,
  };
};
const openEventNewQuestionPublisher: OpenEventNewUpdatePublisher = (
  state,
  { id, groupId, eventId, publisherInstanceId, onBehalfOfSenderId }
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: EventPublisher = {
    ...createEventPublisher(id),
    ...existingState,
    isExpanded: true,
    publisherInstanceId,
    eventId,
    groupId,
    onBehalfOfSenderId,
    postType: 'EventNewQuestion',
    destination: 'Event',
    contentType: 'Question',
    isEditing: false,
    isReply: false,
  };

  return {
    ...state,
    [id]: publisher,
  };
};
const createTeamsMeetingThreadStarterPublisher = (id: Publisher['id']) => ({
  attachments: [],
  hasAttemptedToAttachFile: false,
  changedGroupWhileFilesAttached: false,
  changedPostOnBehalfOfUserWhileFilesAttached: false,
  id,
  isAnnouncement: false,
  isAnonymousMessage: false,
  isModeratorMessage: false,
  isExpanded: true,
  isGifPickerOpen: false,
  isTopicBarOpen: false,
  isTopicBarDismissed: false,
  notifiedUserIds: [],
  praisedUserIds: [],
  pollOptions: [],
  topicNames: [],
  topicIds: [],
  campaignReferences: [],
  inlineAttachmentIds: [],
  isMandatoryNotification: false,
});
type OpenTeamsMeetingPublisher = (
  state: Publishers,
  payload: PublishersOpenTeamsMeetingNewUpdateAction['payload'] | PublishersOpenTeamsMeetingNewQuestionAction['payload']
) => Publishers;
const openTeamsMeetingNewUpdatePublisher: OpenTeamsMeetingPublisher = (
  state,
  { id, teamsMeetingId, publisherInstanceId }
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: TeamsMeetingNewUpdatePublisher = {
    ...createTeamsMeetingThreadStarterPublisher(id),
    ...existingState,
    publisherInstanceId,
    teamsMeetingId,
    postType: 'TeamsMeetingNewUpdate',
    destination: 'TeamsMeeting',
    contentType: 'Regular',
    isEditing: false,
    isReply: false,
  };

  return {
    ...state,
    [id]: publisher,
  };
};
const openTeamsMeetingNewQuestionPublisher: OpenTeamsMeetingPublisher = (
  state,
  { id, teamsMeetingId, publisherInstanceId }
): Publishers => {
  const existingState = state[id] ? state[id] : {};
  const publisher: TeamsMeetingNewQuestionPublisher = {
    ...createTeamsMeetingThreadStarterPublisher(id),
    ...existingState,
    isExpanded: true,
    publisherInstanceId,
    teamsMeetingId,
    postType: 'TeamsMeetingNewQuestion',
    destination: 'TeamsMeeting',
    contentType: 'Question',
    isEditing: false,
    isReply: false,
  };

  return {
    ...state,
    [id]: publisher,
  };
};
const beginReplyToNetworkQuestionMessage = (
  state: Publishers,
  {
    publisherId: id,
    publisherInstanceId,
    threadId,
    messageId,
    emptyPublishers,
    isSecondLevelReply,
  }: PublishersBeginReplyToNetworkQuestionMessageAction['payload']
): Publishers => {
  const existingState = state[id] ? state[id] : {};
  const publisher: NetworkQuestionReplyPublisher = {
    ...createPublisher(id),
    ...existingState,
    publisherInstanceId,
    postType: 'NetworkQuestionReply',
    destination: 'NetworkQuestion',
    contentType: 'Regular',
    isEditing: false,
    isReply: true,
    currentlyReplyingToId: messageId,
    threadId,
    isSecondLevelReply,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
    isExpanded: true,
    isAnnouncement: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};
const beginReplyToUserStorylineMessage = (
  state: Publishers,
  {
    publisherId: id,
    userId,
    publisherInstanceId,
    threadId,
    messageId,
    emptyPublishers,
    isSecondLevelReply,
  }: PublishersBeginReplyToUserStorylineMessageAction['payload']
): Publishers => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserReplyPublisher = {
    ...createPublisher(id),
    ...existingState,
    publisherInstanceId,
    postType: 'UserReply',
    destination: 'User',
    contentType: 'Regular',
    isEditing: false,
    isReply: true,
    currentlyReplyingToId: messageId,
    userId,
    threadId,
    isAnnouncement: false,
    isSecondLevelReply,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
    attachments: [],
    inlineAttachmentIds: [],
    hasAttemptedToAttachFile: false,
    changedGroupWhileFilesAttached: false,
    changedPostOnBehalfOfUserWhileFilesAttached: false,
    headerImage: undefined,
    id,
    isGifPickerOpen: false,
    notifiedUserIds: [],
    topicNames: [],
    topicIds: [],
    isExpanded: true,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};
const beginReplyToGroupMessage = (
  state: Publishers,
  {
    publisherId: id,
    groupId,
    publisherInstanceId,
    threadId,
    messageId,
    emptyPublishers,
    isSecondLevelReply,
    onBehalfOfSenderId,
  }: PublishersBeginReplyToGroupMessageAction['payload']
): Publishers => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupReplyPublisher = {
    ...createPublisher(id),
    ...existingState,
    publisherInstanceId,
    postType: 'GroupReply',
    destination: 'Group',
    contentType: 'Regular',
    isEditing: false,
    isReply: true,
    currentlyReplyingToId: messageId,
    groupId,
    threadId,
    isAnnouncement: false,
    isSecondLevelReply,
    onBehalfOfSenderId,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
    isExpanded: true,
    isMandatoryNotification: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};
const beginReplyToDirectMessage = (
  state: Publishers,
  {
    publisherId: id,
    threadId,
    messageId,
    publisherInstanceId,
    emptyPublishers,
    isSecondLevelReply,
  }: PublishersBeginReplyToDirectMessageAction['payload']
): Publishers => {
  const existingState = state[id] ? state[id] : {};
  const publisher: DirectPublisher & BaseReplyPublisher = {
    ...createPublisher(id),
    ...existingState,
    postType: 'DirectReply',
    destination: 'Direct',
    contentType: 'Regular',
    isEditing: false,
    isReply: true,
    currentlyReplyingToId: messageId,
    threadId,
    publisherInstanceId,
    isAnnouncement: false,
    isSecondLevelReply,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
    isMandatoryNotification: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};
const beginReplyToTeamsMeetingMessage = (
  state: Publishers,
  {
    publisherId: id,
    threadId,
    messageId,
    publisherInstanceId,
    emptyPublishers,
    isSecondLevelReply,
  }: PublishersBeginReplyToTeamsMeetingMessageAction['payload']
): Publishers => {
  const existingState = state[id] ? state[id] : {};
  const publisher: TeamsMeetingReplyPublisher = {
    ...createPublisher(id),
    ...existingState,
    postType: 'TeamsMeetingReply',
    destination: 'TeamsMeeting',
    contentType: 'Regular',
    isEditing: false,
    isReply: true,
    currentlyReplyingToId: messageId,
    threadId,
    publisherInstanceId,
    isAnnouncement: false,
    isSecondLevelReply,
    isTopicBarOpen: false,
    isTopicBarDismissed: false,
    isBestReply: false,
    isModeratorMessage: false,
    isAnonymousMessage: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};
const setQuestionPromptState = (
  state: Publishers,
  { id, questionPromptState }: SetPublisherQuestionPromptAction['payload']
) => updateRecord(state, id, { questionPromptState });
type OpenActionsReducer = (state: Publishers, action: PublishersAction) => Publishers;
export const openActionsReducer: OpenActionsReducer = (state, action) => {
  switch (action.type) {
    case 'PUBLISHER.OPEN_EVENT_NEW_UPDATE':
      return openEventNewUpdatePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_EVENT_NEW_QUESTION':
      return openEventNewQuestionPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_TEAMS_MEETING_NEW_UPDATE':
      return openTeamsMeetingNewUpdatePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_TEAMS_MEETING_NEW_QUESTION':
      return openTeamsMeetingNewQuestionPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_SHARE_TO_NEW_UPDATE':
      return openShareToNewUpdatePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_NEW_UPDATE':
      return openGroupNewUpdatePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_SHARE_NEW_DIRECT':
      return openShareNewDirectPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_NEW_QUESTION':
      return openGroupNewQuestionPublisherWrapper(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_COPILOT_SUGGESTED_QUESTION':
      return openGroupCopilotSuggestedQuestionPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_NEW_ARTICLE':
      return openGroupNewArticlePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_NEW_POLL':
      return openGroupNewPollPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_NEW_PRAISE':
      return openGroupNewPraisePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_REPLY':
      return openGroupReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_DIRECT_NEW':
      return openDirectNewPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_DIRECT_REPLY':
      return openDirectReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_NETWORK_QUESTION_REPLY':
      return openNetworkQuestionReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_TEAMS_MEETING_REPLY':
      return openTeamsMeetingReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_EDIT_MESSAGE':
      return openEditMessagePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_NEW_UPDATE':
      return openUserNewUpdatePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_NEW_MEDIA':
      return openUserNewMediaPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_NEW_QUESTION':
      return openUserNewQuestionPublisherWrapper(state, action.payload);
    case 'PUBLISHER.OPEN_USER_NEW_POLL':
      return openUserNewPollPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_NEW_ARTICLE':
      return openUserNewArticlePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_NEW_PRAISE':
      return openUserNewPraisePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_REPLY':
      return openUserReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_NETWORK_QUESTION_NEW_QUESTION':
      return openNetworkQuestionNewQuestionPublisher(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_USER_STORYLINE_MESSAGE':
      return beginReplyToUserStorylineMessage(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_GROUP_MESSAGE':
      return beginReplyToGroupMessage(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_NETWORK_QUESTION_MESSAGE':
      return beginReplyToNetworkQuestionMessage(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_DIRECT_MESSAGE':
      return beginReplyToDirectMessage(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_TEAMS_MEETING_MESSAGE':
      return beginReplyToTeamsMeetingMessage(state, action.payload);
    case 'PUBLISHER.SET_QUESTION_PROMPT':
      return setQuestionPromptState(state, action.payload);
    default:
      return state;
  }
};
